import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateServiceRecordDto,
  ServiceRecordDetail,
  UpdateServiceRecordDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { useMutationToast } from './mutation-toast';
import { filesKeys } from './files-api';
import { deadlinesKeys } from './deadlines-keys';

/** Step 5.3 service records — every mutation returns the file's full list. */
export async function listServiceRecords(fileId: string): Promise<ServiceRecordDetail[]> {
  const { data } = await api.get<ServiceRecordDetail[]>(`/files/${fileId}/service-records`);
  return data;
}

export const serviceRecordKeys = {
  byFile: (fileId: string) => ['service-records', fileId] as const,
};

export function useServiceRecords(fileId: string) {
  return useQuery({
    queryKey: serviceRecordKeys.byFile(fileId),
    queryFn: () => listServiceRecords(fileId),
    enabled: !!fileId,
  });
}

/**
 * Writing a service record can flip which one is operative, which re-mirrors the
 * service date onto the file — so the file query is invalidated too (the appeal
 * clock and the 5.1 checklist both read it).
 */
function useServiceRecordMutation<TVars>(
  fileId: string,
  fn: (vars: TVars) => Promise<ServiceRecordDetail[]>,
) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: (records) => {
      qc.setQueryData(serviceRecordKeys.byFile(fileId), records);
      qc.invalidateQueries({ queryKey: filesKeys.detail(fileId) });
      qc.invalidateQueries({ queryKey: ['checklist', fileId] });
      // Service starts the appeal clock.
      void qc.invalidateQueries({ queryKey: deadlinesKeys.all });
      toast.success('gov.service.savedToast');
    },
    onError: (err) => toast.error(err, 'gov.service.saveError'),
  });
}

export function useCreateServiceRecord(fileId: string) {
  return useServiceRecordMutation(fileId, async (dto: CreateServiceRecordDto) => {
    const { data } = await api.post<ServiceRecordDetail[]>(`/files/${fileId}/service-records`, dto);
    return data;
  });
}

export function useUpdateServiceRecord(fileId: string) {
  return useServiceRecordMutation(
    fileId,
    async ({ id, dto }: { id: string; dto: UpdateServiceRecordDto }) => {
      const { data } = await api.patch<ServiceRecordDetail[]>(
        `/files/${fileId}/service-records/${id}`,
        dto,
      );
      return data;
    },
  );
}

export function useDeleteServiceRecord(fileId: string) {
  return useServiceRecordMutation(fileId, async (id: string) => {
    const { data } = await api.delete<ServiceRecordDetail[]>(
      `/files/${fileId}/service-records/${id}`,
    );
    return data;
  });
}
